import { useState, useEffect, useCallback, useRef } from "react";
import sceneBg from "@/assets/gate-scene-bg.jpg";

const GateIntro = ({ onComplete }: { onComplete: () => void }) => {
  const [phase, setPhase] = useState<"idle" | "bubble" | "dissolve" | "done">("idle");
  const [showHint, setShowHint] = useState(false);
  const [unmount, setUnmount] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const bubblesRef = useRef<Array<{x:number;y:number;r:number;a:number;speed:number;drift:number;phase:number}>>([]);
  const ambientCtxRef = useRef<AudioContext | null>(null);
  const ambientNodesRef = useRef<AudioNode[]>([]);

  // Arabic ambient music — Hijaz maqam drone + gentle rhythm
  const startAmbientMusic = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      ambientCtxRef.current = ctx;
      const master = ctx.createGain();
      master.gain.setValueAtTime(0, ctx.currentTime);
      master.gain.linearRampToValueAtTime(0.12, ctx.currentTime + 2);
      master.connect(ctx.destination);
      ambientNodesRef.current.push(master);

      // Reverb
      const conv = ctx.createConvolver();
      const rate = ctx.sampleRate;
      const imp = ctx.createBuffer(2, rate * 3, rate);
      for (let ch = 0; ch < 2; ch++) {
        const d = imp.getChannelData(ch);
        for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 3);
      }
      conv.buffer = imp;
      const wetG = ctx.createGain();
      wetG.gain.value = 0.5;
      conv.connect(wetG).connect(master);

      // Deep drone — D2 (fundamental)
      const drone = ctx.createOscillator();
      const droneG = ctx.createGain();
      drone.type = "sine";
      drone.frequency.value = 73.42; // D2
      droneG.gain.value = 0.08;
      drone.connect(droneG).connect(master);
      drone.connect(droneG).connect(conv);
      drone.start();
      ambientNodesRef.current.push(drone);

      // Hijaz maqam drone — D3 with slight detune for warmth
      const drone2 = ctx.createOscillator();
      const drone2G = ctx.createGain();
      drone2.type = "triangle";
      drone2.frequency.value = 146.83; // D3
      drone2.detune.value = 5;
      drone2G.gain.value = 0.04;
      drone2.connect(drone2G).connect(master);
      drone2.connect(drone2G).connect(conv);
      drone2.start();
      ambientNodesRef.current.push(drone2);

      // Hijaz scale notes: D Eb F# G A Bb C# D
      // Gentle melodic phrases — played slowly like a storyteller's oud
      const hijazNotes = [146.83, 155.56, 185.0, 196.0, 220.0, 233.08, 277.18, 293.66];
      const melody = [0, 3, 4, 3, 2, 1, 0, 2, 4, 5, 4, 3, 2, 0, 7, 4, 3, 2, 1, 0];
      
      melody.forEach((noteIdx, i) => {
        const t = ctx.currentTime + 1.5 + i * 1.2;
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        const filt = ctx.createBiquadFilter();
        osc.type = "triangle";
        osc.frequency.value = hijazNotes[noteIdx];
        // Gentle vibrato
        const vib = ctx.createOscillator();
        const vibG = ctx.createGain();
        vib.frequency.value = 5;
        vibG.gain.value = 2;
        vib.connect(vibG).connect(osc.frequency);
        vib.start(t);
        vib.stop(t + 1.0);

        filt.type = "lowpass";
        filt.frequency.value = 800;
        filt.Q.value = 2;

        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.035, t + 0.15);
        g.gain.linearRampToValueAtTime(0.025, t + 0.5);
        g.gain.exponentialRampToValueAtTime(0.001, t + 1.1);
        osc.connect(filt).connect(g).connect(master);
        g.connect(conv);
        osc.start(t);
        osc.stop(t + 1.1);
      });

      // Soft percussion — like a darbuka tap pattern
      const percPattern = [0, 0.6, 1.0, 1.2, 1.8, 2.4, 2.6, 3.0, 3.6, 4.2, 4.4, 4.8];
      const percAccent = [1, 0.4, 0.6, 0.3, 1, 0.4, 0.6, 0.3, 1, 0.4, 0.6, 0.3];
      for (let rep = 0; rep < 4; rep++) {
        percPattern.forEach((beat, i) => {
          const t = ctx.currentTime + 0.8 + rep * 5.4 + beat;
          // Filtered noise burst for percussion
          const bufLen = Math.floor(rate * 0.06);
          const noiseBuf = ctx.createBuffer(1, bufLen, rate);
          const data = noiseBuf.getChannelData(0);
          for (let j = 0; j < bufLen; j++) data[j] = (Math.random() * 2 - 1) * Math.pow(1 - j / bufLen, 4);
          
          const src = ctx.createBufferSource();
          src.buffer = noiseBuf;
          const pG = ctx.createGain();
          const pF = ctx.createBiquadFilter();
          pF.type = "bandpass";
          pF.frequency.value = percAccent[i] > 0.8 ? 200 : 400;
          pF.Q.value = 3;
          pG.gain.value = 0.03 * percAccent[i];
          src.connect(pF).connect(pG).connect(master);
          src.start(t);
        });
      }
    } catch {}
  }, []);

  // Fade out ambient music
  const fadeOutAmbient = useCallback(() => {
    if (ambientCtxRef.current) {
      const ctx = ambientCtxRef.current;
      const master = ambientNodesRef.current[0] as GainNode;
      if (master?.gain) {
        master.gain.linearRampToValueAtTime(0, ctx.currentTime + 1.5);
      }
      setTimeout(() => {
        ambientNodesRef.current.forEach(n => { try { (n as OscillatorNode).stop?.(); } catch {} });
        try { ctx.close(); } catch {}
      }, 2000);
    }
  }, []);

  // Dreamy ambient sound — low warm pad + soft chime
  const playDreamSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const convolver = ctx.createConvolver();
      const rate = ctx.sampleRate;
      const len = rate * 3;
      const impulse = ctx.createBuffer(2, len, rate);
      for (let ch = 0; ch < 2; ch++) {
        const d = impulse.getChannelData(ch);
        for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3);
      }
      convolver.buffer = impulse;
      const wet = ctx.createGain();
      wet.gain.value = 0.6;
      convolver.connect(wet).connect(ctx.destination);

      // Deep warm pad
      const pad = ctx.createOscillator();
      const padG = ctx.createGain();
      pad.type = "sine";
      pad.frequency.value = 174; // F3 — dreamy
      padG.gain.setValueAtTime(0, ctx.currentTime);
      padG.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 0.6);
      padG.gain.linearRampToValueAtTime(0.05, ctx.currentTime + 1.5);
      padG.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 3);
      pad.connect(padG);
      padG.connect(convolver);
      padG.connect(ctx.destination);
      pad.start();
      pad.stop(ctx.currentTime + 3);

      // Soft ascending chimes (delayed, subtle)
      [392, 523.25, 659.25, 783.99].forEach((freq, i) => {
        const t = ctx.currentTime + 0.3 + i * 0.2;
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.06, t + 0.08);
        g.gain.exponentialRampToValueAtTime(0.001, t + 1.5);
        osc.connect(g);
        g.connect(convolver);
        g.connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 1.5);
      });
    } catch {}
  }, []);

  // Floating thought bubbles
  const startBubbles = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const cx = canvas.width / 2;
    const cy = canvas.height * 0.52;

    for (let i = 0; i < 30; i++) {
      bubblesRef.current.push({
        x: cx + (Math.random() - 0.5) * 60,
        y: cy + (Math.random() - 0.5) * 40,
        r: 3 + Math.random() * 12,
        a: 0,
        speed: 0.3 + Math.random() * 0.8,
        drift: (Math.random() - 0.5) * 0.6,
        phase: Math.random() * Math.PI * 2,
      });
    }

    let t = 0;
    const animate = () => {
      const c = canvas.getContext("2d");
      if (!c) return;
      c.clearRect(0, 0, canvas.width, canvas.height);
      t += 0.016;

      bubblesRef.current.forEach(b => {
        b.y -= b.speed;
        b.x += b.drift + Math.sin(t * 2 + b.phase) * 0.3;
        b.a = Math.min(b.a + 0.015, 0.4);
        if (b.y < -20) b.a *= 0.95;

        // Iridescent bubble
        const grad = c.createRadialGradient(b.x - b.r * 0.3, b.y - b.r * 0.3, 0, b.x, b.y, b.r);
        grad.addColorStop(0, `hsla(42, 70%, 90%, ${b.a * 0.8})`);
        grad.addColorStop(0.5, `hsla(42, 60%, 75%, ${b.a * 0.3})`);
        grad.addColorStop(1, `hsla(42, 50%, 60%, 0)`);
        c.beginPath();
        c.arc(b.x, b.y, b.r, 0, Math.PI * 2);
        c.fillStyle = grad;
        c.fill();

        // Shine
        c.beginPath();
        c.arc(b.x - b.r * 0.25, b.y - b.r * 0.25, b.r * 0.2, 0, Math.PI * 2);
        c.fillStyle = `hsla(0, 0%, 100%, ${b.a * 0.6})`;
        c.fill();
      });

      bubblesRef.current = bubblesRef.current.filter(b => b.a > 0.005);
      if (bubblesRef.current.length > 0) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []);

  const startSequence = useCallback(() => {
    if (phase !== "idle") return;
    playDreamSound();
    fadeOutAmbient();
    startBubbles();

    // Phase 1: bubble — scene softens, vignette closes like eyes closing
    setPhase("bubble");

    // Phase 2: dissolve — everything melts away dreamily
    setTimeout(() => setPhase("dissolve"), 1400);

    // Phase 3: done
    setTimeout(() => {
      setPhase("done");
      onComplete();
    }, 2400);

    setTimeout(() => setUnmount(true), 3000);
  }, [phase, onComplete, playDreamSound, fadeOutAmbient, startBubbles]);

  // Start ambient music on first user interaction (needed for autoplay policy)
  useEffect(() => {
    let started = false;
    const handleInteraction = () => {
      if (!started && phase === "idle") {
        started = true;
        startAmbientMusic();
      }
    };
    // Try on any touch/click/mousemove
    window.addEventListener("pointerdown", handleInteraction, { once: true });
    window.addEventListener("touchstart", handleInteraction, { once: true });
    return () => {
      window.removeEventListener("pointerdown", handleInteraction);
      window.removeEventListener("touchstart", handleInteraction);
    };
  }, [phase, startAmbientMusic]);

  useEffect(() => {
    const t = setTimeout(() => setShowHint(true), 600);
    return () => clearTimeout(t);
  }, []);

  if (unmount) return null;

  const isBubble = phase === "bubble";
  const isDissolve = phase === "dissolve" || phase === "done";
  const isActive = phase !== "idle";

  return (
    <div
      className="fixed inset-0 z-[9999] overflow-hidden"
      role="presentation"
      style={{
        opacity: phase === "done" ? 0 : 1,
        transition: phase === "done" ? "opacity 0.8s ease-in-out" : "none",
        pointerEvents: isDissolve ? "none" : "auto",
      }}
    >
      {/* Scene background — slow gentle zoom + dreamy blur */}
      <div className="absolute inset-0 w-full h-full" style={{
        backgroundImage: `url(${sceneBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center 35%",
        transform: isDissolve ? "scale(1.15)" : isBubble ? "scale(1.08)" : "scale(1.02)",
        filter: isDissolve
          ? "blur(30px) brightness(1.3) saturate(0.2)"
          : isBubble
            ? "blur(4px) brightness(1.1) saturate(0.8)"
            : "none",
        transition: isBubble
          ? "transform 1.8s cubic-bezier(0.25, 0, 0.25, 1), filter 1.4s ease-out"
          : isDissolve
            ? "transform 1.2s ease-out, filter 1s ease-out"
            : "transform 0.3s ease-out",
        transformOrigin: "50% 40%",
      }} />

      {/* Dreamy vignette — closing like eyelids */}
      <div className="absolute inset-0 pointer-events-none z-10" style={{
        background: isDissolve
          ? "radial-gradient(ellipse at 50% 50%, transparent 0%, rgba(15,10,5,0.7) 40%, rgba(5,2,0,0.95) 70%)"
          : isBubble
            ? "radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(15,10,5,0.4) 70%, rgba(5,2,0,0.8) 100%)"
            : "radial-gradient(ellipse at 50% 35%, transparent 25%, hsl(20 30% 5% / 0.5) 100%)",
        transition: "background 1.2s ease-in-out",
      }} />

      {/* Warm dreamy glow from center — like a thought forming */}
      <div className="absolute inset-0 pointer-events-none z-15" style={{
        background: "radial-gradient(circle at 50% 52%, hsla(42,60%,80%,0.3) 0%, hsla(42,50%,70%,0.1) 25%, transparent 50%)",
        opacity: isBubble ? 1 : isDissolve ? 0.5 : 0,
        transform: isDissolve ? "scale(2)" : isBubble ? "scale(1.2)" : "scale(0.5)",
        transition: "opacity 1s ease-out, transform 1.5s ease-out",
      }} />

      {/* Bubble canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-20" />

      {/* Letterbox */}
      <div className="absolute top-0 left-0 right-0 h-[6%] bg-black/60 z-10"
        style={{ opacity: isActive ? 0 : 1, transition: "opacity 0.8s ease-out" }} />
      <div className="absolute bottom-0 left-0 right-0 h-[6%] bg-black/60 z-10"
        style={{ opacity: isActive ? 0 : 1, transition: "opacity 0.8s ease-out" }} />

      {/* Brand text */}
      <div className={`absolute inset-0 flex flex-col items-center z-10 ${
        isActive ? "opacity-0" : "opacity-100"
      }`} style={{
        paddingTop: "10%",
        transform: isBubble ? "scale(0.95) translateY(-10px)" : isDissolve ? "scale(0.9) translateY(-20px)" : "scale(1)",
        filter: isBubble ? "blur(3px)" : isDissolve ? "blur(10px)" : "none",
        transition: "all 1.2s cubic-bezier(0.25, 0, 0.25, 1)",
      }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="h-px w-8 bg-gradient-to-r from-transparent to-white/60" />
          <div className="w-1.5 h-1.5 rotate-45 bg-white/50" />
          <div className="h-px w-8 bg-gradient-to-l from-transparent to-white/60" />
        </div>

        <p className="text-xs md:text-sm tracking-[0.5em] uppercase text-white/90 mb-2"
          style={{ fontFamily: "var(--font-body)", textShadow: "0 2px 12px rgba(0,0,0,0.8)" }}>
          Bienvenue chez
        </p>

        <h1 className="text-5xl md:text-8xl font-bold mb-2"
          style={{
            fontFamily: "var(--font-display)",
            color: "white",
            textShadow: "0 4px 20px rgba(0,0,0,0.7), 0 2px 6px rgba(0,0,0,0.5), 0 0 60px hsl(42 72% 55% / 0.25)",
            letterSpacing: "0.05em",
          }}>
          EL FAZA
        </h1>

        <p className="text-sm md:text-lg tracking-[0.3em] uppercase text-white/80"
          style={{ fontFamily: "var(--font-body)", textShadow: "0 2px 12px rgba(0,0,0,0.8)" }}>
          L'art tunisien en relief
        </p>
      </div>

      {/* Glowing dot + CTA */}
      <div className={`absolute z-20 flex flex-col items-center gap-3 ${
        isActive ? "opacity-0 scale-50" : showHint ? "opacity-100 scale-100" : "opacity-0 scale-50"
      }`} style={{
        left: "50%", top: "52%", transform: "translate(-50%, -50%)",
        transition: "all 0.5s cubic-bezier(0.25, 0, 0.25, 1)",
      }}>
        <button
          onClick={startSequence}
          className="relative flex items-center justify-center group cursor-pointer"
          aria-label="Entrer sur le site"
        >
          <div className="absolute w-14 h-14 rounded-full border border-white/20"
            style={{ animation: "dotRingPulse 2s ease-out infinite" }} />
          <div className="absolute w-10 h-10 rounded-full border border-white/30"
            style={{ animation: "dotRingPulse 2s ease-out 0.6s infinite" }} />

          <div className="w-7 h-7 rounded-full bg-white/20 backdrop-blur-sm border border-white/50
            group-hover:bg-white/30 group-hover:border-white/70 group-hover:scale-110
            transition-all duration-200 flex items-center justify-center"
            style={{
              boxShadow: "0 0 15px hsl(42 80% 65% / 0.5), 0 0 30px hsl(42 80% 65% / 0.2)",
            }}>
            <div className="w-2.5 h-2.5 rounded-full bg-white"
              style={{
                boxShadow: "0 0 8px white, 0 0 16px hsl(42 80% 70%)",
                animation: "dotGlow 1.5s ease-in-out infinite",
              }} />
          </div>
        </button>

        <p className="text-xs md:text-sm tracking-[0.2em] uppercase text-white/90 whitespace-nowrap"
          style={{ fontFamily: "var(--font-body)", textShadow: "0 2px 10px rgba(0,0,0,0.8)" }}>
          Tape pour commencer ton aventure
        </p>
      </div>
    </div>
  );
};

export default GateIntro;
